import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  Textarea,
  useClipboard,
  useToast,
} from "@chakra-ui/react";
import {
  DEFAULT_BASIC_ITEMS,
  DEFAULT_STORE_VALUES,
  DEFAULT_TOAST_DURATION,
  MAX_GENKYU_KEKKIN_NISSU,
  RoundingMethodList,
} from "@kg-pos/common";
import {
  CreateStoreInput,
  RoundingMethod,
  StoreResponse,
  UpdateStoreInput,
} from "__generated__/graphql";
import { RequireField } from "components/atoms/require-field";
import { StoreSubmit } from "interfaces";
import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";

type StoreFormProps = {
  submit: (input: CreateStoreInput | UpdateStoreInput) => Promise<void>;
  data?: StoreResponse;
};

export const StoreForm: React.VFC<StoreFormProps> = ({ submit, data }) => {
  const { goBack } = useHistory();
  const toast = useToast();
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    getValues,
    setValue,
  } = useForm({
    defaultValues: {
      name: data?.name,
      description: data?.description,
      receiptPrinterIpAddress: data?.receiptPrinterIpAddress,
      serviceFee: data?.serviceFee,
      orderItemLockTimeHours: data?.orderItemLockTime?.substring(0, 2),
      orderItemLockTimeMinutes: data?.orderItemLockTime?.substring(2, 4),
      staffReservationFee: data?.staffReservationFee ?? DEFAULT_STORE_VALUES.staffReservationFee,
      defaultTableChargeCount:
        data?.defaultTableChargeCount ?? DEFAULT_STORE_VALUES.defaultTableChargeCount,
      notificationEmailAddress: data?.notificationEmailAddress,
      companyName: data?.companyName,
      address: data?.address,
      invoiceNumber: data?.invoiceNumber,
      kaikinNgAmount: data?.kaikinNgAmount ?? DEFAULT_STORE_VALUES.kaikinNgAmount,
      naikinUriage: data?.naikinUriage ?? DEFAULT_STORE_VALUES.naikinUriage,
      naikinKaikinSho: data?.naikinKaikinSho ?? DEFAULT_STORE_VALUES.naikinKaikinSho,
      genkyuKekkinNissu: data?.genkyuKekkinNissu ?? DEFAULT_STORE_VALUES.genkyuKekkinNissu,
      firstServe: data?.firstServe ?? DEFAULT_STORE_VALUES.firstServe,
      amountRoundingMethod: data?.amountRoundingMethod ?? DEFAULT_STORE_VALUES.amountRoundingMethod,
    },
  });
  const { hasCopied, onCopy } = useClipboard(data?.id ?? "");
  const isEdit = useMemo((): boolean => {
    return !!data;
  }, [data]);
  const [hasGenkyuKekkinNissu, setHasGenkyuKekkinNissu] = useState(
    getValues("genkyuKekkinNissu") < MAX_GENKYU_KEKKIN_NISSU
  );

  const onSubmit = async (input: StoreSubmit): Promise<void> => {
    const { orderItemLockTimeHours, orderItemLockTimeMinutes, ...rest } = input;
    const orderItemLockTime = `${orderItemLockTimeHours}${orderItemLockTimeMinutes}`;
    await submit({ ...rest, orderItemLockTime });

    toast({
      title: isEdit ? `${input.name}を編集しました。` : `${input.name}を新規作成しました。`,
      status: "success",
      duration: DEFAULT_TOAST_DURATION,
      isClosable: true,
    });

    goBack();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {!!data && (
        <FormControl mb={6}>
          <FormLabel htmlFor="storeId">
            店舗ID
            <RequireField />
          </FormLabel>
          <Flex>
            <Input value={data.id} isDisabled id="storeId" maxW="400px" />
            <Button onClick={onCopy} ml={2}>
              {hasCopied ? "Copied" : "Copy"}
            </Button>
          </Flex>
        </FormControl>
      )}
      <FormControl isInvalid={!!errors.name}>
        <FormLabel htmlFor="name">
          店舗名
          <RequireField />
        </FormLabel>
        <Input
          id="name"
          placeholder="店舗名を入力してください。"
          maxW="400px"
          {...register("name", {
            required: "店舗名が必要です。",
            maxLength: {
              value: 100,
              message: "100文字以下で入力してください。",
            },
          })}
        />
        <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isInvalid={!!errors.description} mt={6}>
        <FormLabel htmlFor="description">説明</FormLabel>
        <Input
          id="description"
          placeholder="説明を入力してください。"
          maxW="400px"
          {...register("description", {
            maxLength: {
              value: 500,
              message: "500文字以下で入力してください。",
            },
          })}
        />
        <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isInvalid={!!errors.receiptPrinterIpAddress} mt={6}>
        <FormLabel htmlFor="receiptPrinterIpAddress">レシートプリンタのIPアドレス</FormLabel>
        <Input
          id="receiptPrinterIpAddress"
          placeholder="例）192.168.192.168"
          maxW="400px"
          {...register("receiptPrinterIpAddress", {
            maxLength: {
              value: 100,
              message: "100文字以下で入力してください。",
            },
          })}
        />
        <FormErrorMessage>{errors.receiptPrinterIpAddress?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isInvalid={!!errors.serviceFee} mt={6}>
        <FormLabel htmlFor="serviceFee">
          サービス料(%)
          <RequireField />
        </FormLabel>
        <Input
          id="serviceFee"
          type="number"
          placeholder="サービス料を入力してください。"
          maxW="400px"
          {...register("serviceFee", {
            valueAsNumber: true,
            required: "サービス料が必要です。",
            min: { value: 0, message: "0以上で入力してください。" },
            max: { value: 100, message: "100以下で入力してください。" },
            pattern: { value: /^\d+/, message: "数字で入力してください。" },
          })}
        />
        <FormErrorMessage>{errors.serviceFee?.message}</FormErrorMessage>
      </FormControl>
      <FormControl
        id="orderItemLockTime"
        isInvalid={!!errors.orderItemLockTimeHours || !!errors.orderItemLockTimeMinutes}
        mt={6}
        maxW="400px"
      >
        <FormLabel>
          注文ロック時間
          <RequireField />
        </FormLabel>
        <Flex>
          <Select
            defaultValue="23"
            pr={1}
            {...register("orderItemLockTimeHours", {
              required: "注文ロック時間を選択してください。",
            })}
          >
            {[
              "00",
              "01",
              "02",
              "03",
              "04",
              "05",
              "06",
              "07",
              "08",
              "09",
              "10",
              "11",
              "12",
              "13",
              "14",
              "15",
              "16",
              "17",
              "18",
              "19",
              "20",
              "21",
              "22",
              "23",
            ].map((option, key) => (
              <option key={key} value={option}>
                {option}
              </option>
            ))}
          </Select>
          <Select
            defaultValue="45"
            pl={1}
            {...register("orderItemLockTimeMinutes", {
              required: "注文ロック時間を選択してください。",
            })}
          >
            {["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"].map(
              (option, key) => (
                <option key={key} value={option}>
                  {option}
                </option>
              )
            )}
          </Select>
        </Flex>
        <FormErrorMessage>{errors.orderItemLockTimeHours?.message}</FormErrorMessage>
        <FormErrorMessage>{errors.orderItemLockTimeMinutes?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isInvalid={!!errors.staffReservationFee} mt={6}>
        <FormLabel htmlFor="staffReservationFee">
          指名料
          <RequireField />
        </FormLabel>
        <Input
          id="staffReservationFee"
          type="number"
          placeholder="指名料を入力してください。"
          maxW="400px"
          {...register("staffReservationFee", {
            valueAsNumber: true,
            required: "指名料が必要です。",
            min: {
              value: 0,
              message: "0円以上で入力してください。",
            },
            max: {
              value: 50_000_000,
              message: "5000万円以下で入力してください。",
            },
            pattern: { value: /^\d+/, message: "数字で入力してください。" },
          })}
        />
        <FormErrorMessage>{errors.staffReservationFee?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isInvalid={!!errors.defaultTableChargeCount} mt={6}>
        <FormLabel htmlFor="defaultTableChargeCount">
          {DEFAULT_BASIC_ITEMS.tableCharge.itemName}数量
          <RequireField />
        </FormLabel>
        <Input
          id="defaultTableChargeCount"
          type="number"
          placeholder={`${DEFAULT_BASIC_ITEMS.tableCharge.itemName}数量を入力してください。`}
          maxW="400px"
          {...register("defaultTableChargeCount", {
            valueAsNumber: true,
            required: `${DEFAULT_BASIC_ITEMS.tableCharge.itemName}数量が必要です。`,
            min: {
              value: 0,
              message: "0以上で入力してください。",
            },
            max: {
              value: 100,
              message: "100以下で入力してください。",
            },
            pattern: { value: /^\d+/, message: "数字で入力してください。" },
          })}
        />
        <FormErrorMessage>{errors.defaultTableChargeCount?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isInvalid={!!errors.notificationEmailAddress} mt={6}>
        <FormLabel htmlFor="notificationEmailAddress">注文通知メールアドレス</FormLabel>
        <Input
          id="notificationEmailAddress"
          type="email"
          placeholder="メールアドレスを入力してください。"
          maxW="400px"
          {...register("notificationEmailAddress", {
            maxLength: {
              value: 100,
              message: "100文字以下で入力してください。",
            },
          })}
        />
        <FormErrorMessage>{errors.notificationEmailAddress?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isInvalid={!!errors.companyName} mt={6}>
        <FormLabel htmlFor="companyName">株式会社名</FormLabel>
        <Input
          id="companyName"
          placeholder="「株式会社」を含めて入力してください。"
          maxW="400px"
          {...register("companyName", {
            maxLength: {
              value: 100,
              message: "100文字以下で入力してください。",
            },
          })}
        />
        <FormErrorMessage>{errors.companyName?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isInvalid={!!errors.address} mt={6}>
        <FormLabel>住所</FormLabel>
        <Textarea
          placeholder="全角20文字(半角40文字)程度まででの改行を推奨"
          h="3.5rem"
          maxW="400px"
          background="grey.300"
          {...register("address", {
            maxLength: { value: 500, message: "500文字以下で入力してください。" },
          })}
        />
        <FormErrorMessage>{errors.address?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isInvalid={!!errors.invoiceNumber} mt={6}>
        <FormLabel htmlFor="invoiceNumber">インボイス登録番号</FormLabel>
        <Input
          id="invoiceNumber"
          placeholder="例）T4011101059648"
          maxW="400px"
          {...register("invoiceNumber", {
            maxLength: {
              value: 100,
              message: "100文字以下で入力してください。",
            },
          })}
        />
        <FormErrorMessage>{errors.invoiceNumber?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isInvalid={!!errors.kaikinNgAmount} mt={6}>
        <FormLabel htmlFor="kaikinNgAmount">
          皆勤NG金額
          <RequireField />
        </FormLabel>
        <Input
          id="kaikinNgAmount"
          type="number"
          placeholder="皆勤NG金額を入力してください。"
          maxW="400px"
          {...register("kaikinNgAmount", {
            valueAsNumber: true,
            required: "皆勤NG金額が必要です。",
            min: {
              value: 0,
              message: "0円以上で入力してください。",
            },
            max: {
              value: 50_000_000,
              message: "5000万円以下で入力してください。",
            },
            pattern: { value: /^\d+/, message: "数字で入力してください。" },
          })}
        />
        <FormErrorMessage>{errors.kaikinNgAmount?.message}</FormErrorMessage>
      </FormControl>
      <FormControl isInvalid={!!errors.naikinUriage} mt={6}>
        <Checkbox {...register("naikinUriage")}>内勤売上</Checkbox>
      </FormControl>
      <FormControl isInvalid={!!errors.naikinKaikinSho} mt={6}>
        <Checkbox {...register("naikinKaikinSho")}>内勤皆勤賞</Checkbox>
      </FormControl>
      <FormControl mt={6}>
        <Checkbox
          defaultChecked={hasGenkyuKekkinNissu}
          onChange={(e) => {
            // 見た目上はチェックボックスで使用有無を制御しつつ、内部的には値変更で対応する
            // (チェックと数値で項目を2つに増やしたくないので)
            setHasGenkyuKekkinNissu(e.target.checked);
            if (e.target.checked) {
              setValue("genkyuKekkinNissu", DEFAULT_STORE_VALUES.genkyuKekkinNissu);
            } else {
              setValue("genkyuKekkinNissu", MAX_GENKYU_KEKKIN_NISSU);
            }
          }}
        >
          減給欠勤日数
        </Checkbox>
      </FormControl>
      {hasGenkyuKekkinNissu && (
        <FormControl isInvalid={!!errors.genkyuKekkinNissu} mt={6}>
          <FormLabel htmlFor="genkyuKekkinNissu">
            減給欠勤日数
            <RequireField />
          </FormLabel>
          <Input
            id="genkyuKekkinNissu"
            type="number"
            placeholder="減給欠勤日数を入力してください。"
            maxW="400px"
            {...register("genkyuKekkinNissu", {
              valueAsNumber: true,
              required: "減給欠勤日数が必要です。",
              min: {
                value: 1,
                message: "1日以上で入力してください。",
              },
              max: {
                value: MAX_GENKYU_KEKKIN_NISSU,
                message: `${MAX_GENKYU_KEKKIN_NISSU}日以下で入力してください。`,
              },
              pattern: { value: /^\d+/, message: "数字で入力してください。" },
            })}
          />
          <FormErrorMessage>{errors.genkyuKekkinNissu?.message}</FormErrorMessage>
        </FormControl>
      )}
      <FormControl isInvalid={!!errors.firstServe} mt={6}>
        <Checkbox {...register("firstServe")}>ファーストサーブ</Checkbox>
      </FormControl>
      <FormControl isInvalid={!!errors.amountRoundingMethod} mt={6}>
        <FormLabel htmlFor="amountRoundingMethod">
          端数処理方法
          <RequireField />
        </FormLabel>
        <Select
          placeholder="端数処理方法を選択してください。"
          {...register("amountRoundingMethod", {
            required: "端数処理方法を選択してください。",
          })}
          maxW="400px"
        >
          <option value={RoundingMethod.ROUND}>{RoundingMethodList[RoundingMethod.ROUND]}</option>
          <option value={RoundingMethod.CEIL}>{RoundingMethodList[RoundingMethod.CEIL]}</option>
        </Select>
        <FormErrorMessage>{errors.amountRoundingMethod?.message}</FormErrorMessage>
      </FormControl>
      <Button mt={8} colorScheme="blue" isLoading={isSubmitting} type="submit">
        {isEdit ? "編集する" : "登録する"}
      </Button>
    </form>
  );
};
